import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import {
  ActionButton,
  AddItemButton,
  Alert,
  ConfirmationDialog,
  NewFormSection as FormSection,
  Hidden,
  ActiveSwitch,
  Table,
  TableBody,
  TableRow,
  Typography,
  useDialogs,
  SettingsPage,
  BoltIcon
} from '@paintscout/ui'
import { Form, useFormikContext } from 'formik'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import type {
  AutomationOption,
  QuoteStatusUpdateTriggerConfig,
  UpdateDealStageActionConfig
} from '@paintscout/util/builder'
import { getAutomations, getObjectLabels, getPipelines, uuid } from '@paintscout/util/builder'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { AutomationOptionDialog, AutomationRenameDialog } from '../../dialogs'
import {
  automationIncomplete,
  getDefaultActionConfig
} from '../../dialogs/AutomationOptionDialog/AutomationOptionDialog'

const useStyles = makeStyles<Theme, AutomationsProps>(
  (theme) => ({
    root: {},
    tableRow: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '4px',
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      marginBottom: theme.spacing(2),
      padding: `${theme.spacing(2)}px ${theme.spacing(0.5)}px`
    },
    tableDiv: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      maxWidth: '100%',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'flex-start'
      }
    },
    tableDivLeft: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    tableDivRight: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    rowLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        paddingRight: theme.spacing(2)
      }
    },
    rowTitleText: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start'
    },
    titleText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    boltIcon: {
      fontSize: '1.8rem',
      marginTop: `-${theme.spacing(2)}px`,
      marginRight: `${theme.spacing(1)}px`
    },
    arrowIcon: {
      fontSize: '2rem',
      margin: `${theme.spacing(0)}px ${theme.spacing(0.5)}px`,
      [theme.breakpoints.down('xs')]: {
        marginTop: -10,
        marginBottom: -10
      }
    },
    switch: {
      justifySelf: 'flex-end'
    },
    alertDiv: {
      margin: `${theme.spacing(2)}px ${theme.spacing(2)}px -${theme.spacing(2)}px ${theme.spacing(2)}px`
    }
  }),
  { name: 'Automations' }
)

export interface AutomationsProps {
  classes?: StyleClasses<typeof useStyles>
  onDialogConfirm?: () => void
}

function Automations(props: AutomationsProps) {
  const { values, setFieldValue } = useFormikContext<{
    options: OptionsDocument
    rates: RatesDocument
  }>()
  const { options } = values
  const classes = useStyles(props)
  const automations = getAutomations({ options, forOptions: true })
  const objectLabels = getObjectLabels({ options })
  const pipelines = getPipelines({ options })
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()

  function onAdd() {
    const newAutomation = createAutomationOption()
    openDialog(AutomationOptionDialog, {
      isNew: true,
      automation: newAutomation,
      options,
      onConfirm: (automation: AutomationOption) => {
        setFieldValue('options.options.automations', {
          ...options?.options?.automations,
          values: {
            ...options?.options?.automations?.values,
            [automation.id]: automation
          },
          order: [automation.id, ...(options.options?.automations?.order ?? [])]
        })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleAction = (action: string, automation: AutomationOption) => {
    switch (action) {
      case 'edit':
        openDialog(AutomationOptionDialog, {
          automation,
          options,
          onConfirm: (updatedAutomation: AutomationOption) => {
            setFieldValue('options.options.automations', {
              ...options.options.automations,
              values: {
                ...options.options.automations.values,
                [automation.id]: updatedAutomation
              }
            })
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'rename':
        openDialog(AutomationRenameDialog, {
          automation,
          onConfirm: (updatedAutomation: AutomationOption) => {
            setFieldValue('options.options.automations', {
              ...options.options.automations,
              values: {
                ...options.options.automations.values,
                [automation.id]: updatedAutomation
              }
            })
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'duplicate':
        // eslint-disable-next-line no-case-declarations
        const copiedAutomation = {
          ...automation,
          id: uuid(),
          name: automation.name.length ? `${automation.name} (Copy)` : ''
        }
        setFieldValue('options.options.automations', {
          ...options.options.automations,
          values: {
            ...options.options.automations.values,
            [copiedAutomation.id]: copiedAutomation
          },
          order: [...options.options.automations.order, copiedAutomation.id]
        })
        dismissDialog()
        break
      case 'delete':
        openDialog(ConfirmationDialog, {
          title: 'Delete Automation',
          message: `Are you sure you want to delete the automation? This action cannot be undone, and this automation will no longer run.`,
          onConfirm: () => {
            setFieldValue('options.options.automations', {
              ...options.options.automations,
              values: Object.fromEntries(
                Object.entries(options.options.automations.values).map(([key, value]) =>
                  key === automation.id ? [key, { ...value, deleted: true }] : [key, value]
                )
              )
            })
            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
    }
  }

  const handleActionActiveToggle = (automation: AutomationOption) => {
    // can only update automation to active if it has all its valid fields
    if (automationIncomplete(automation)) {
      enqueueSnackbar('Cannot toggle automation active state, please complete all required fields.', {
        variant: 'error'
      })
    } else {
      setFieldValue('options.options.automations', {
        ...options.options.automations,
        values: {
          ...options.options.automations.values,
          [automation.id]: {
            ...automation,
            active: !automation.active
          }
        }
      })
    }
  }

  function getActionText(automation: AutomationOption) {
    let res = ''
    if (automation.actions[0].type === 'update-deal-stage') {
      const automationPipeline = pipelines.find(
        (p) => p.key === (automation.actions[0].config as UpdateDealStageActionConfig)?.targetPipeline?.value
      )
      const pipelineStageName =
        automationPipeline?.stages?.find(
          (stage) => stage.key === (automation.actions[0].config as UpdateDealStageActionConfig)?.targetStage?.value
        )?.label ?? '*Invalid Stage*'
      res = `Move Deal to ${pipelineStageName}`
    } else if (automation.actions[0].type === 'add-tags') {
      res = `Add Tag(s) to docs`
    } else {
      res = 'Unknown action'
    }
    return res
  }

  function getTriggerText(automation: AutomationOption) {
    let res = ''
    const triggerTargetStatusName = (automation.trigger.config as QuoteStatusUpdateTriggerConfig).targetStatus
      .value as string
    switch (automation.trigger.type) {
      case 'quote-status-changed':
      case 'invoice-status-changed': {
        const formattedTriggerStatusName = triggerTargetStatusName === 'onHold' ? 'On Hold' : triggerTargetStatusName
        res = `${automation.trigger.type === 'invoice-status-changed' ? 'Invoice' : objectLabels.quote.value} ${formattedTriggerStatusName[0].toUpperCase() + formattedTriggerStatusName.slice(1)}`
        break
      }
      default:
        res = 'Unknown trigger'
    }
    return res
  }

  return (
    <SettingsPage
      title={'Automations'}
      callout={{
        path: 'settings-automations',
        content: 'Save time by setting up custom automations that keep deals moving through your pipeline.',
        learnMoreLink: 'http://help.paintscout.com/en/articles/11879246-automations'
      }}
    >
      <Form>
        <FormSection topPadded={automations.length === 0}>
          <Table>
            <TableBody>
              {automations.map((automation: AutomationOption) => {
                // Need to generate these intellegently based on the automation
                const automationTriggerText = getTriggerText(automation)
                const automationActionText = getActionText(automation)
                return (
                  <TableRow
                    key={automation.id}
                    className={classes.tableRow}
                    onClick={() => handleAction('edit', automation)}
                  >
                    <div className={classes.tableDiv}>
                      <div className={classes.tableDivLeft}>
                        <BoltIcon className={classes.boltIcon} />
                        <div className={classes.rowLabel}>
                          <Hidden xsDown>
                            <div className={classes.rowTitleText}>
                              {automation.name.length > 0 ? (
                                <Typography variant={'h4'} className={classes.titleText}>
                                  {automation.name}
                                </Typography>
                              ) : (
                                <Typography variant={'h4'} className={classes.titleText}>
                                  {automationTriggerText}
                                  <ArrowRightAltIcon className={classes.arrowIcon} />
                                  {automationActionText}
                                </Typography>
                              )}
                            </div>
                          </Hidden>
                          <Hidden smUp>
                            <Typography variant={'h4'}>
                              {automationTriggerText}
                              <ArrowRightAltIcon className={classes.arrowIcon} />
                              {automationActionText}
                            </Typography>
                          </Hidden>
                          <Typography variant={'subtitle2'} color={'textSecondary'}>
                            {automation?.updated ? 'Updated' : 'Created'} on{' '}
                            {moment(automation?.updated ?? automation.created).format('DD/MM/YYYY')}
                          </Typography>
                        </div>
                      </div>
                      <div className={classes.tableDivRight}>
                        <ActiveSwitch
                          className={classes.switch}
                          checked={automation.active && !automationActionText.includes('*Invalid Stage*')}
                          onClick={(ev) => ev.stopPropagation()}
                          onChange={() => handleActionActiveToggle(automation)}
                          color="primary"
                          name="active"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                          disabled={automationActionText.includes('*Invalid Stage*')}
                        />
                        <ActionButton
                          data-testid="actions"
                          actions={[
                            {
                              key: 'edit',
                              label: 'Edit'
                            },
                            {
                              key: 'rename',
                              label: 'Rename'
                            },
                            {
                              key: 'duplicate',
                              label: 'Duplicate'
                            },
                            {
                              key: 'delete',
                              label: 'Delete',
                              scary: true
                            }
                          ]}
                          onActionClick={(ev, action) => handleAction(action, automation)}
                        />
                      </div>
                    </div>
                    {automationActionText.includes('*Invalid Stage*') && (
                      <div className={classes.alertDiv}>
                        <Alert
                          severity={'error'}
                          content={`This automation is no longer working because the seleted deal stage has been removed. Please review and update the automation to restore functionality.`}
                        />
                      </div>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <AddItemButton className={classes.addButton} onClick={onAdd}>
            New Automation
          </AddItemButton>
        </FormSection>
      </Form>
    </SettingsPage>
  )
}

export default Automations

function createAutomationOption(): AutomationOption {
  return {
    name: '',
    id: uuid(),
    active: true,
    created: Date.now(),
    trigger: null,
    // Until we get more actions default create the first one to be deal stage update
    actions: [getDefaultActionConfig('update-deal-stage')]
  }
}
